(function (global) {
  'use strict';
  const SEP = '\u001f';
  const getField = (product, name) => product.fields.find(field => field.label === name);
  const getChoice = (field, value) => field && field.choices.find(choice => choice.label === value);
  const sizeAlias = size => size === 'King Size' ? 'King' : size;
  function hydrate(catalog) {
    if (!catalog.choiceSets) return catalog;
    for (const product of Object.values(catalog.products)) {
      if (product.variants) continue;
      for (const field of product.fields) field.choices = catalog.choiceSets[field.choiceSet];
      const sizes = getField(product, 'Size').choices;
      const fabrics = getField(product, 'Fabric & Colour').choices;
      const unavailable = new Set(product.unavailable);
      product.variants = {};
      for (const [compact, id] of Object.entries(product.variantIds)) {
        const [size, fabric, configuration] = compact.split('/').map(Number);
        const key = [sizes[size].label, fabrics[fabric].label, catalog.configurationNames[configuration]].join(SEP);
        product.variants[key] = { id, price: product.nativePrices[size + '/' + configuration], available: !unavailable.has(compact) };
      }
    }
    return catalog;
  }
  function activeFields(product, state) {
    const suffix = '(' + sizeAlias(state.Size) + ')';
    return product.fields.filter(field => !field.label.startsWith('Add ') || field.label.endsWith(suffix));
  }
  function resolve(product, state) {
    const fields = activeFields(product, state);
    const choices = {};
    for (const field of fields) {
      const choice = getChoice(field, state[field.label]);
      if (!choice) return { valid: false, reason: 'required', field: field.label };
      choices[field.label] = choice;
    }
    if (!choices.Size || !choices['Fabric & Colour']) return { valid: false, reason: 'required' };
    const storageField = fields.find(field => field.label.startsWith('Add Lift Up Storage'));
    const tvField = fields.find(field => field.label.startsWith('Add TV Footend'));
    const storage = storageField && choices[storageField.label].label === 'Yes';
    const tvLabel = tvField ? choices[tvField.label].label : 'No';
    const tv = tvLabel === 'No' ? '' : tvLabel.includes('Detailed') ? 'Detailed' : 'Plain';
    const configuration = storage ? (tv ? 'Lift-Up Storage + TV – ' + tv : 'Lift-Up Storage') : (tv ? 'TV Footend – ' + tv : 'Standard Bed');
    const key = [state.Size, state['Fabric & Colour'], configuration].join(SEP);
    const variant = product.variants[key];
    if (!variant) return { valid: false, reason: 'unavailable' };
    let nativePrice = product.basePrice;
    for (const field of fields) {
      if (field.label !== 'Headboard Height') nativePrice += choices[field.label].price;
    }
    if (nativePrice !== variant.price) return { valid: false, reason: 'price' };
    const heightCharge = choices['Headboard Height'] ? choices['Headboard Height'].price : 0;
    const properties = {};
    for (const field of fields) {
      if (!['Size', 'Fabric & Colour'].includes(field.label)) properties[field.label] = choices[field.label].label;
    }
    const quote = choices['Headboard Height'] && choices['Headboard Height'].label === 'Specific Custom Height';
    if (quote) {
      if (!(state.customHeight || '').trim()) return { valid: false, reason: 'customHeight' };
      properties['Requested custom height'] = state.customHeight.trim().slice(0, 200);
      properties['Headboard quote'] = 'A quote will be provided for the requested custom height.';
    }
    return { valid: variant.available, reason: variant.available ? '' : 'unavailable', variant, configuration, heightCharge, price: nativePrice + heightCharge, properties, quote: !!quote };
  }
  function buildItems(result, catalog, quantity, group) {
    if (!result.valid || !Number.isInteger(quantity) || quantity < 1) throw new Error('Invalid configuration');
    const properties = Object.assign({}, result.properties, { _tlbc_mirror_group: group });
    const items = [{ id: result.variant.id, quantity, properties }];
    if (result.heightCharge) {
      const id = catalog.heightCharges[String(result.heightCharge)];
      if (!id) throw new Error('Missing height upgrade');
      properties._tlbc_height_charge = String(result.heightCharge);
      properties._tlbc_height_variant = String(id);
      items.push({ id, quantity, properties: { 'Headboard Height': properties['Headboard Height'], _tlbc_parent_group: group } });
    }
    return items;
  }
  function planCartRepairs(cart) {
    const changes = [];
    const beds = cart.items.filter(item => item.properties && item.properties._tlbc_mirror_group);
    const upgrades = cart.items.filter(item => item.properties && item.properties._tlbc_parent_group);
    for (const upgrade of upgrades) {
      const bed = beds.find(item => item.properties._tlbc_mirror_group === upgrade.properties._tlbc_parent_group);
      if (!bed || Number(bed.properties._tlbc_height_variant) !== upgrade.variant_id) changes.push({ id: upgrade.key, quantity: 0 });
      else if (upgrade.quantity !== bed.quantity) changes.push({ id: upgrade.key, quantity: bed.quantity });
    }
    for (const bed of beds) {
      if (!bed.properties._tlbc_height_charge) continue;
      const upgrade = upgrades.find(item => item.properties._tlbc_parent_group === bed.properties._tlbc_mirror_group && item.variant_id === Number(bed.properties._tlbc_height_variant));
      if (!upgrade) {
        const properties = Object.assign({}, bed.properties);
        properties['Headboard Height'] = '72" (6ft) - Standard Height';
        delete properties._tlbc_height_charge;
        delete properties._tlbc_height_variant;
        changes.push({ id: bed.key, quantity: bed.quantity, properties, resetHeight: true });
      }
    }
    return changes;
  }
  const api = { hydrate, activeFields, resolve, buildItems, planCartRepairs };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document === 'undefined') return;
  global.TLBCMirror = api;
  const root = (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
  const ui = global.TLBCMirrorText || {};
  const money = value => new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: 'GBP' }).format(value / 100);
  const text = key => ui[key] || key;
  const el = (tag, className, content) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (content !== undefined) node.textContent = content;
    return node;
  };
  async function request(path, body) {
    const response = await fetch(root + path, { method: body ? 'POST' : 'GET', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, ...(body ? { body: JSON.stringify(body) } : {}) });
    const data = await response.json();
    if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || text('error'));
    return data;
  }
  let catalogPromise;
  function getCatalog(url) {
    if (!catalogPromise) catalogPromise = fetch(url).then(response => {
      if (!response.ok) throw new Error(text('error'));
      return response.json();
    }).then(hydrate);
    return catalogPromise;
  }
  async function initialise(container) {
    if (container.dataset.initialised) return;
    container.dataset.initialised = 'true';
    const form = document.getElementById(container.dataset.formId);
    const error = form.querySelector('[data-tlbc-error]');
    const buttons = [...form.querySelectorAll('button[data-tlbc-submit]')];
    let state = {}, busy = false;
    try {
      const catalog = await getCatalog(container.dataset.catalogUrl);
      const product = catalog.products[container.dataset.productId];
      if (!product) throw new Error(text('error'));
      container.replaceChildren();
      const controls = new Map();
      const selected = container.dataset.variantId && Object.entries(product.variants).find(([, value]) => String(value.id) === container.dataset.variantId);
      if (selected) {
        const parts = selected[0].split(SEP);
        state.Size = parts[0]; state['Fabric & Colour'] = parts[1];
        for (const field of product.fields) {
          if (field.label.startsWith('Add Lift Up Storage') && field.label.endsWith('(' + sizeAlias(state.Size) + ')')) state[field.label] = parts[2].startsWith('Lift-Up Storage') ? 'Yes' : 'No';
          if (field.label.startsWith('Add TV Footend') && field.label.endsWith('(' + sizeAlias(state.Size) + ')')) state[field.label] = parts[2].includes('TV') ? field.choices.find(choice => choice.label.includes(parts[2].includes('Detailed') ? 'Detailed' : 'Plain')).label : 'No';
        }
      }
      // Match source ordering while making fabric selection visible near size.
      const ordered = [...product.fields].sort((a, b) => {
        const rank = field => field.label === 'Size' ? 0 : field.label === 'Fabric & Colour' ? 1 : field.label.startsWith('Add Lift') ? 2 : field.label === 'Headboard Height' ? 3 : field.label === 'Piping Colour' ? 4 : field.label.startsWith('Add TV') ? 5 : 6;
        return rank(a) - rank(b);
      });
      for (const field of ordered) {
        const wrapper = el('fieldset', 'tlbc-field');
        const legend = el('legend', 'tlbc-label', field.label);
        wrapper.append(legend);
        if (field.description) wrapper.append(el('p', 'tlbc-description', field.description));
        const single = field.choices.length === 1;
        if (single) {
          const choice = field.choices[0];
          if (choice.image) { const image = el('img', 'tlbc-ack-image'); image.src = choice.image; image.alt = field.label; image.loading = 'lazy'; wrapper.append(image); }
          const label = el('label', 'tlbc-ack');
          const input = el('input'); input.type = 'checkbox'; input.dataset.field = field.label; input.value = choice.label;
          label.append(input, document.createTextNode(choice.label)); wrapper.append(label);
        } else if (field.choices.length > 10) {
          const selectedLabel = el('p', 'tlbc-selected', state[field.label] || text('choose'));
          const details = el('details', 'tlbc-swatches'); details.open = field.label === 'Fabric & Colour';
          const summary = el('summary', '', text('showColours')); details.append(summary);
          const grid = el('div', 'tlbc-grid');
          for (const choice of field.choices) {
            const label = el('label', 'tlbc-swatch');
            const input = el('input'); input.type = 'radio'; input.name = 'tlbc-' + container.id + '-' + field.label; input.value = choice.label; input.dataset.field = field.label; input.checked = state[field.label] === choice.label;
            label.append(input);
            if (choice.image) { const image = el('img'); image.src = choice.image; image.alt = choice.label; image.width = 64; image.height = 64; image.loading = 'lazy'; label.append(image); }
            label.append(el('span', '', choice.label)); grid.append(label);
          }
          details.append(grid); wrapper.append(selectedLabel, details); wrapper.selectedLabel = selectedLabel;
        } else {
          const select = el('select', 'tlbc-select'); select.dataset.field = field.label; select.setAttribute('aria-label', field.label);
          const placeholder = el('option', '', text('choose')); placeholder.value = ''; select.append(placeholder);
          for (const choice of field.choices) {
            const charge = choice.price && !choice.label.includes('£') ? ' (+' + money(choice.price) + ')' : '';
            const option = el('option', '', choice.label + charge); option.value = choice.label; option.selected = state[field.label] === choice.label; select.append(option);
          }
          wrapper.append(select);
          const choiceDescription = el('p', 'tlbc-description'); wrapper.append(choiceDescription); wrapper.choiceDescription = choiceDescription;
          if (field.label === 'Headboard Height' && field.choices.some(choice => choice.label === 'Specific Custom Height')) {
            const customLabel = el('label', 'tlbc-custom', text('customHeight'));
            const input = el('input', 'tlbc-select'); input.type = 'text'; input.maxLength = 200; input.dataset.customHeight = 'true'; input.placeholder = text('heightExample'); customLabel.append(input); wrapper.append(customLabel); wrapper.customLabel = customLabel;
          }
        }
        container.append(wrapper); controls.set(field.label, wrapper);
      }
      const total = el('p', 'tlbc-total'); total.setAttribute('role', 'status'); container.append(total);
      const hint = el('p', 'tlbc-description'); hint.setAttribute('role', 'status'); container.append(hint);
      function update() {
        const active = new Set(activeFields(product, state).map(field => field.label));
        for (const field of product.fields) {
          const control = controls.get(field.label);
          control.hidden = !active.has(field.label) || (field.label.startsWith('Add ') && !state.Size);
          if (control.selectedLabel) control.selectedLabel.textContent = state[field.label] || text('choose');
          if (control.choiceDescription) control.choiceDescription.textContent = (getChoice(field, state[field.label]) || {}).description || '';
          if (control.customLabel) control.customLabel.hidden = state[field.label] !== 'Specific Custom Height';
        }
        const result = resolve(product, state);
        let visiblePrice = product.basePrice;
        for (const field of activeFields(product, state)) visiblePrice += (getChoice(field, state[field.label]) || {}).price || 0;
        total.textContent = text('total') + ': ' + money(result.valid ? result.price : visiblePrice);
        const price = document.getElementById('price-' + container.dataset.sectionId);
        if (price) { price.textContent = money(result.valid ? result.price : visiblePrice); price.classList.add('tlbc-price'); }
        hint.textContent = result.valid ? (result.quote ? text('quote') : text('selected')) : result.reason === 'price' ? text('priceError') : result.reason === 'unavailable' ? text('unavailable') : text('complete');
        buttons.forEach(button => { button.disabled = busy || !result.valid; });
        form.querySelector('input[name="id"]').value = result.valid ? result.variant.id : '';
        return result;
      }
      container.addEventListener('change', event => {
        const input = event.target;
        if (input.dataset.field) {
          const field = input.dataset.field;
          state[field] = input.type === 'checkbox' && !input.checked ? '' : input.value;
          if (field === 'Size') {
            for (const entry of product.fields.filter(value => value.label.startsWith('Add '))) {
              state[entry.label] = '';
              const select = controls.get(entry.label).querySelector('select'); if (select) select.value = '';
            }
          }
          error.textContent = ''; update();
        }
      });
      container.addEventListener('input', event => { if (event.target.dataset.customHeight) { state.customHeight = event.target.value; update(); } });
      form.addEventListener('submit', async event => {
        event.preventDefault();
        const result = update();
        if (busy || !result.valid) return;
        const quantityInput = document.querySelector('input[name="quantity"][form="' + container.dataset.formId + '"]');
        const quantity = quantityInput ? Number(quantityInput.value) : 1;
        if (!Number.isInteger(quantity) || quantity < 1) { error.textContent = text('quantityError'); return; }
        busy = true; update(); error.textContent = text('adding');
        const group = global.crypto && global.crypto.randomUUID ? global.crypto.randomUUID() : Date.now() + '-' + Math.random().toString(36).slice(2);
        try {
          await request('cart/add.js', { items: buildItems(result, catalog, quantity, group) });
          const cart = await request('cart.js');
          const bed = cart.items.find(item => item.properties && item.properties._tlbc_mirror_group === group);
          const upgrade = cart.items.find(item => item.properties && item.properties._tlbc_parent_group === group);
          if (!bed || bed.original_price !== result.variant.price || bed.quantity !== quantity || (result.heightCharge && (!upgrade || upgrade.original_price !== result.heightCharge || upgrade.quantity !== quantity))) throw new Error(text('priceError'));
          if (result.quote) await request('cart/update.js', { note: [cart.note, product.name + ': ' + state.customHeight.trim() + '. ' + text('quote')].filter(Boolean).join('\n') });
          global.location.assign(root + (event.submitter && event.submitter.dataset.tlbcSubmit === 'checkout' ? 'checkout' : 'cart'));
        } catch (failure) {
          // Remove only this failed attempt, leaving earlier basket items intact.
          try {
            const cart = await request('cart.js');
            const updates = {};
            cart.items.filter(item => item.properties && (item.properties._tlbc_mirror_group === group || item.properties._tlbc_parent_group === group)).forEach(item => { updates[item.key] = 0; });
            if (Object.keys(updates).length) await request('cart/update.js', { updates });
          } catch (_) { /* The visible error asks the customer to retry. */ }
          error.textContent = failure.message || text('error');
          busy = false; update();
        }
      });
      update();
    } catch (failure) { error.textContent = failure.message || text('error'); buttons.forEach(button => { button.disabled = true; }); }
  }
  let repairPromise;
  function repairCart() {
    if (repairPromise) return repairPromise;
    repairPromise = (async () => {
      let cart = await request('cart.js');
      const changes = planCartRepairs(cart);
      for (const change of changes) {
        const payload = { id: change.id, quantity: change.quantity };
        if (change.properties) payload.properties = change.properties;
        cart = await request('cart/change.js', payload);
      }
      if (planCartRepairs(cart).length) throw new Error(text('error'));
      return changes;
    })().finally(() => { repairPromise = null; });
    return repairPromise;
  }
  let checkoutPending = false;
  let cartSubscribed = false;
  let cartInitialised = false;
  async function synchroniseCartView() {
    try {
      const changes = await repairCart();
      if (changes.length && !checkoutPending) global.location.reload();
    } catch (failure) {
      const error = document.getElementById('cart-errors');
      if (error) error.textContent = failure.message || text('error');
    }
  }
  document.addEventListener('click', async event => {
    const checkout = event.target.closest('button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]');
    if (!checkout || checkout.closest('[data-tlbc-form]')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (checkoutPending) return;
    checkoutPending = true;
    checkout.setAttribute('aria-busy', 'true');
    try {
      const repairs = await repairCart();
      if (repairs.some(change => change.resetHeight)) {
        global.alert(text('resetHeight'));
        global.location.assign(root + 'cart');
      } else global.location.assign(root + 'checkout');
    } catch (failure) { global.alert(failure.message || text('error')); }
    finally { checkoutPending = false; checkout.removeAttribute('aria-busy'); }
  }, true);
  function boot() {
    if (!cartSubscribed && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      cartSubscribed = true;
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        if (document.querySelector('cart-items')) synchroniseCartView();
      });
    }
    if (!cartInitialised && document.querySelector('cart-items')) {
      cartInitialised = true;
      synchroniseCartView();
    }
    document.querySelectorAll('[data-tlbc-mirror]').forEach(initialise);
    document.querySelectorAll('[data-tlbc-gallery]').forEach(gallery => {
      if (gallery.dataset.initialised) return;
      gallery.dataset.initialised = 'true';
      const image = gallery.querySelector('.tlbc-gallery-main img');
      const dialog = gallery.querySelector('dialog');
      gallery.addEventListener('click', event => {
        const thumbnail = event.target.closest('[data-tlbc-image]');
        if (thumbnail) {
          image.removeAttribute('srcset'); image.src = thumbnail.dataset.tlbcImage;
          gallery.querySelectorAll('[data-tlbc-image]').forEach(button => button.setAttribute('aria-pressed', String(button === thumbnail)));
        } else if (event.target.closest('[data-tlbc-open-image]')) {
          dialog.querySelector('img').src = image.currentSrc || image.src;
          dialog.showModal();
        } else if (event.target.closest('[data-tlbc-close-image]') || event.target === dialog) dialog.close();
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})(typeof window === 'undefined' ? globalThis : window);
